import React, { useEffect, useState } from 'react';
import { cn, getResponderBadge } from '@/lib/utils';
import { CircleUserRound, User2, X } from 'lucide-react';
import { Badge, Button } from '..';
import { constants } from '@/lib';
import { useERSContext } from '@/context';
import { useTranslation } from 'react-i18next';

export const SOSModal = ({ isVisible = true, onClose, onAssist, user, location, incident, lastStatus, sosData }) => {
  if (!isVisible) return null;
  const { t } = useTranslation();
  const { incidentAssigneeStatusList, latestResponder } = useERSContext();

  const [statusIdToDisplayNameMap, setStatusIdToDisplayNameMap] = useState({});

  useEffect(() => {
    if (!isVisible) return;

    if (incidentAssigneeStatusList?.length) {
      const map = incidentAssigneeStatusList.reduce((acc, status) => {
        acc[status.id] = status.display_name;
        return acc;
      }, {});
      setStatusIdToDisplayNameMap(map);
    }
  }, [isVisible, sosData]);

  const handleResponderStatus = id => {
    let filterResponders = latestResponder?.filter(responders => responders?.assignee?.id.split('@')[0] == id);
    return getResponderBadge(filterResponders?.[0], statusIdToDisplayNameMap);
  };

  return (
    <div
      className={cn(
        'fixed inset-0 z-[9999] flex items-center justify-center bg/70 backdrop-blur-sm',
        'animate-in fade-in duration-300'
      )}
    >
      <div
        className={cn(
          'relative w-96 rounded-xl border border-error-strong bg shadow-lg p-5 transition-all',
          'animate-in fade-in-zoom duration-300'
        )}
      >
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            {/* FIXED: Centered Avatar + Dot */}
            {user.avatar ? (
              <img
                src={user.avatar}
                alt={user.name}
                className="h-8 w-8 rounded-full object-cover border border-error-strong"
              />
            ) : (
              <div className="relative h-8 w-8 rounded-full flex items-center justify-center bg-muted border border-error-strong">
                <CircleUserRound size={18} className="text-muted-foreground" />
                <span className="absolute bottom-0 right-0 block h-2 w-2 border rounded-full bg-error-strong"></span>
              </div>
            )}

            <p className="text-sm text-text font-medium leading-tight">
              <span className="font-semibold">{user.name}</span>{' '}
              {t('SOS_MODAL.TITLE_ALERT_TRIGGERED') && (
                <>
                  {' '}
                  {t('SOS_MODAL.TITLE_ALERT_TRIGGERED').split(' ')[0]}{' '}
                  <span className="text-error-strong font-semibold">
                    {t('SOS_MODAL.TITLE_ALERT_TRIGGERED').split(' ').slice(1).join(' ')}
                  </span>
                </>
              )}
            </p>
          </div>

          <Button
            variant="ghost"
            size="ghostIcon"
            onClick={onClose}
            className="text-text hover:bg-error-strong/10 hover:text-error-strong transition"
          >
            <X size={16} />
          </Button>
        </div>

        {/* Body */}
        <div className="mt-4 space-y-3 text-sm">
          <div className="gap-1">
            <p className="text-text">{t('SOS_MODAL.LOCATION_LABEL')}</p>
            <p className="text-muted">{location}</p>
          </div>

          <div className="grid grid-cols-2 w-full">
            <div className="flex flex-col gap-1">
              <p className="text-text">{t('SOS_MODAL.INCIDENT_LABEL')}</p>
              <p className="text-muted">{incident}</p>
            </div>

            <div className="flex flex-col gap-1 items-start">
              <p className="text-text">{t('SOS_MODAL.LAST_STATUS_LABEL')}</p>
              <Badge variant={handleResponderStatus(sosData?.userId).variant} size="sm" className="text-xs">
                {t('RESPONDER_STATUS', { returnObjects: true }).find(
                  status => status.value === handleResponderStatus(sosData?.userId).label
                )?.label || handleResponderStatus(sosData?.userId).label}
              </Badge>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-5 flex justify-end">
          <Button variant="destructive" size="sm" onClick={onAssist}>
            {t('SOS_MODAL.ASSIST_BUTTON')}
          </Button>
        </div>
      </div>
    </div>
  );
};
